// ── WhatsAppCompliancePage ─────────────────────────────────────────
//
// Compliance management:
//   • Opt-in list stats + CSV export
//   • Bulk CSV import (drag-and-drop)
//   • Single manual opt-in
//   • Embeddable widget snippet generator
//
// All API operations scoped to the selected WhatsApp account.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Code2,
  Copy,
  Download,
  FileSpreadsheet,
  Loader2,
  Phone,
  Plus,
  Shield,
  Upload,
  Users,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { toast } from '@/components/ui/toast';
import api from '@/lib/api';
import {
  listWhatsappAccounts,
  type WhatsappAccountListItem,
} from '../api/whatsapp.api';
import { SelectField } from '@/components/ui/SelectField';

// ── Opt-in stats (from prisma count on backend — we use accounts endpoint
//    to get account details, and a simple count API) ─────────────────────

// ── API helpers not in whatsapp.api.ts yet (thin wrappers) ────────

async function addSingleOptIn(body: {
  whatsappAccountId: string;
  contactWaId: string;
  source: string;
  consentText?: string;
}) {
  const res = await api.post('/whatsapp/opt-ins', body);
  return res.data;
}

async function bulkImportOptIns(
  whatsappAccountId: string,
  file: File,
): Promise<{
  total: number;
  added: number;
  updated: number;
  skipped: number;
  errors: { row: number; reason: string; value?: string }[];
}> {
  const form = new FormData();
  form.append('whatsappAccountId', whatsappAccountId);
  form.append('file', file);
  const res = await api.post('/whatsapp/opt-ins/bulk', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

async function exportOptIns(accountId: string): Promise<void> {
  const res = await api.get('/whatsapp/opt-ins/export', {
    params: { accountId },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download =
    (res.headers['content-disposition']?.match(/filename="(.+)"/)?.[1]) ??
    `optins_${accountId}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

async function getApiKeys(): Promise<{ id: string; name: string; key: string }[]> {
  try {
    const res = await api.get('/api-keys');
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

// ── Embeddable widget snippet ──────────────────────────────────────

function buildSnippet(
  apiKey: string,
  wabaAccountId: string,
  apiBaseUrl: string,
): string {
  return `<!-- BrixLead WhatsApp Opt-In Widget -->
<div id="brixlead-optin"></div>
<script>
(function() {
  var container = document.getElementById('brixlead-optin');
  var html = '<form id="bl-optin-form" style="font-family:sans-serif;max-width:340px">' +
    '<h3 style="margin:0 0 12px">Get WhatsApp Updates</h3>' +
    '<label style="display:block;margin-bottom:4px;font-size:14px">Phone number (with country code)</label>' +
    '<input id="bl-phone" type="tel" placeholder="+919876543210" required style="width:100%;padding:8px;border:1px solid #ccc;border-radius:6px;margin-bottom:12px;box-sizing:border-box">' +
    '<label style="display:flex;align-items:flex-start;gap:8px;font-size:13px;cursor:pointer;margin-bottom:12px">' +
    '  <input id="bl-consent" type="checkbox" required style="margin-top:2px">' +
    '  I consent to receive WhatsApp messages. I can opt out at any time.' +
    '</label>' +
    '<button type="submit" style="background:#25D366;color:#fff;border:none;padding:10px 20px;border-radius:6px;cursor:pointer;font-size:14px;font-weight:600;width:100%">Subscribe</button>' +
    '<p id="bl-msg" style="margin:8px 0 0;font-size:13px"></p>' +
  '</form>';
  container.innerHTML = html;

  document.getElementById('bl-optin-form').addEventListener('submit', function(e) {
    e.preventDefault();
    var phone = document.getElementById('bl-phone').value.replace(/[^0-9+]/g, '').replace(/^\\+/, '');
    var consent = document.getElementById('bl-consent').checked;
    var msg = document.getElementById('bl-msg');
    if (!phone || !consent) { msg.style.color='red'; msg.textContent='Please fill all fields.'; return; }
    msg.style.color=''; msg.textContent='Submitting…';
    fetch('${apiBaseUrl}/api/whatsapp/opt-ins/public', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': '${apiKey}' },
      body: JSON.stringify({
        whatsappAccountId: '${wabaAccountId}',
        contactWaId: phone,
        consent: true,
        consentText: 'Web widget — user ticked consent checkbox'
      })
    }).then(function(r) {
      if (r.ok) { msg.style.color='green'; msg.textContent='You are subscribed!'; document.getElementById('bl-optin-form').reset(); }
      else { msg.style.color='red'; msg.textContent='Error. Please try again.'; }
    }).catch(function() { msg.style.color='red'; msg.textContent='Network error.'; });
  });
})();
</script>
<!-- End BrixLead Widget -->`;
}

// ── Single opt-in form ─────────────────────────────────────────────

function AddOptInModal({
  accountId,
  onClose,
  onAdded,
}: {
  accountId: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 6 || digits.length > 15) {
      setError('Enter a valid phone number (6-15 digits).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await addSingleOptIn({
        whatsappAccountId: accountId,
        contactWaId: digits,
        source: 'MANUAL',
        consentText: 'Manually added via BrixLead dashboard',
      });
      toast('success', 'Opt-in recorded');
      onAdded();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to add opt-in');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl">
        <h3 className="mb-4 text-base font-semibold text-slate-800">Add single opt-in</h3>
        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-normal text-slate-600">
            Phone number (with country code, no +)
          </label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder="919876543210"
            className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm font-mono"
            autoFocus
          />
        </div>
        {error && (
          <p className="mb-3 flex items-start gap-1.5 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            {error}
          </p>
        )}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-md border border-gray-200 py-2 text-sm text-slate-600"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-indigo-600 py-2 text-sm font-medium text-white disabled:bg-slate-300"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Add opt-in
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────

export default function WhatsAppCompliancePage() {
  const [accounts, setAccounts] = useState<WhatsappAccountListItem[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState<any | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [apiKeys, setApiKeys] = useState<{ id: string; name: string; key: string }[]>([]);
  const [selectedApiKey, setSelectedApiKey] = useState('');
  const [snippetCopied, setSnippetCopied] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    Promise.all([listWhatsappAccounts(), getApiKeys()])
      .then(([accs, keys]) => {
        setAccounts(accs);
        if (accs.length > 0 && !selectedAccount) setSelectedAccount(accs[0].id);
        setApiKeys(keys);
        if (keys.length > 0) setSelectedApiKey(keys[0].key);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleFileImport = async (file: File) => {
    if (!selectedAccount) return;
    if (!file.name.endsWith('.csv')) {
      toast('error', 'Only CSV files are supported');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const result = await bulkImportOptIns(selectedAccount, file);
      setImportResult(result);
      toast(
        result.errors.length > 0 ? 'error' : 'success',
        `Imported ${result.added} new, updated ${result.updated}${result.skipped > 0 ? `, ${result.skipped} skipped (see errors)` : ''}`,
      );
    } catch (e: any) {
      toast('error', e?.response?.data?.message ?? 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    if (!selectedAccount) return;
    setExporting(true);
    try {
      await exportOptIns(selectedAccount);
    } catch {
      toast('error', 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileImport(file);
  };

  const snippet =
    selectedAccount && selectedApiKey
      ? buildSnippet(selectedApiKey, selectedAccount, window.location.origin.replace(':5173', ':3000'))
      : '';

  const copySnippet = async () => {
    if (!snippet) return;
    await navigator.clipboard.writeText(snippet);
    setSnippetCopied(true);
    setTimeout(() => setSnippetCopied(false), 2000);
  };

  if (loading) {
    return (
      <DashboardLayout title="WhatsApp Compliance">
        <div className="flex justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="WhatsApp Compliance">
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-800">Compliance &amp; Opt-ins</h1>
            <p className="text-sm text-slate-500">
              Manage WhatsApp consent records. Only opted-in contacts can receive broadcasts.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {accounts.length > 1 && (
              <SelectField
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                className="rounded-md border border-gray-200 px-3 py-1.5 text-sm"
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.displayPhoneNumber}
                  </option>
                ))}
              </SelectField>
            )}
            <button
              onClick={() => setShowAddModal(true)}
              disabled={!selectedAccount}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              <Plus className="h-4 w-4" />
              Add opt-in
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || !selectedAccount}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Export CSV
            </button>
          </div>
        </div>

        {/* Compliance summary */}
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            {
              icon: Shield,
              label: 'Compliance status',
              value: 'Active',
              sub: 'Opt-outs processed automatically',
              color: 'text-emerald-600',
              bg: 'bg-emerald-50',
            },
            {
              icon: Users,
              label: 'Opt-in contacts',
              value: '—',
              sub: 'Visible in CSV export',
              color: 'text-blue-600',
              bg: 'bg-blue-50',
            },
            {
              icon: Phone,
              label: 'Phone number',
              value: accounts.find((a) => a.id === selectedAccount)?.displayPhoneNumber ?? '—',
              sub: 'Selected account',
              color: 'text-slate-600',
              bg: 'bg-slate-50',
            },
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.label}
                className={`flex items-start gap-3 rounded-xl p-4 ${stat.bg} border border-gray-100`}
              >
                <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${stat.color}`} />
                <div>
                  <p className="text-xs text-slate-500">{stat.label}</p>
                  <p className={`text-base font-semibold ${stat.color}`}>{stat.value}</p>
                  <p className="text-[11px] text-slate-400">{stat.sub}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bulk CSV import */}
        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-semibold text-slate-700">Bulk CSV Import</h2>
          </div>

          {/* Drag-and-drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed py-10 transition-colors ${
              dragOver
                ? 'border-indigo-400 bg-indigo-50'
                : 'border-gray-200 bg-slate-50 hover:border-indigo-300 hover:bg-indigo-50/50'
            }`}
          >
            {importing ? (
              <Loader2 className="mb-2 h-8 w-8 animate-spin text-indigo-400" />
            ) : (
              <Upload className="mb-2 h-8 w-8 text-slate-300" />
            )}
            <p className="text-sm font-medium text-slate-600">
              {importing ? 'Importing…' : 'Drop CSV here or click to upload'}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              Columns required: <code className="font-mono">phone, consent</code>. Optional:{' '}
              <code className="font-mono">name</code>. Max 10,000 rows · 5 MB.
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileImport(file);
              e.target.value = '';
            }}
          />

          {/* Template download */}
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={() => {
                const csv = 'phone,consent,name\n919876543210,true,Ravi Kumar\n919812345678,yes,';
                const a = document.createElement('a');
                a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                a.download = 'optins_template.csv';
                a.click();
              }}
              className="text-xs text-indigo-600 hover:underline"
            >
              Download CSV template
            </button>
          </div>

          {/* Import result */}
          {importResult && (
            <div className="mt-4 rounded-lg border border-gray-100 bg-slate-50 p-4">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="text-slate-600">
                  Total: <strong>{importResult.total}</strong>
                </span>
                <span className="text-emerald-600">
                  Added: <strong>{importResult.added}</strong>
                </span>
                <span className="text-blue-600">
                  Updated: <strong>{importResult.updated}</strong>
                </span>
                {importResult.skipped > 0 && (
                  <span className="text-amber-600">
                    Skipped: <strong>{importResult.skipped}</strong>
                  </span>
                )}
              </div>
              {importResult.errors?.length > 0 && (
                <div className="mt-3">
                  <p className="mb-1 text-xs font-medium text-red-600">
                    {importResult.errors.length} row error{importResult.errors.length > 1 ? 's' : ''}:
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-0.5">
                    {importResult.errors.slice(0, 20).map((err: any, i: number) => (
                      <p key={i} className="text-[11px] text-red-600">
                        Row {err.row}: {err.reason}
                        {err.value ? ` (${err.value})` : ''}
                      </p>
                    ))}
                    {importResult.errors.length > 20 && (
                      <p className="text-[11px] text-red-500">
                        +{importResult.errors.length - 20} more errors…
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Widget snippet */}
        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <Code2 className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-semibold text-slate-700">Embeddable Opt-In Widget</h2>
          </div>
          <p className="mb-4 text-sm text-slate-500">
            Embed this snippet on your website to collect WhatsApp opt-ins. The widget posts
            consent directly to BrixLead using your API key.
          </p>

          {/* Config selectors */}
          <div className="mb-4 grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-normal text-slate-600">
                WhatsApp Account
              </label>
              <SelectField
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm"
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.displayPhoneNumber}
                  </option>
                ))}
              </SelectField>
            </div>
            <div>
              <label className="mb-1 block text-xs font-normal text-slate-600">API Key</label>
              {apiKeys.length === 0 ? (
                <p className="text-xs text-amber-600">
                  No API keys found.{' '}
                  <a href="/developer" className="text-indigo-600 hover:underline">
                    Create one
                  </a>{' '}
                  in the Developer page.
                </p>
              ) : (
                <SelectField
                  value={selectedApiKey}
                  onChange={(e) => setSelectedApiKey(e.target.value)}
                  className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                >
                  {apiKeys.map((k) => (
                    <option key={k.id} value={k.key}>
                      {k.name}
                    </option>
                  ))}
                </SelectField>
              )}
            </div>
          </div>

          {snippet ? (
            <div className="relative">
              <pre className="max-h-64 overflow-y-auto rounded-lg bg-slate-900 p-4 text-[11px] leading-relaxed text-slate-100 whitespace-pre-wrap">
                {snippet}
              </pre>
              <button
                onClick={copySnippet}
                className={`absolute right-2 top-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                  snippetCopied
                    ? 'bg-emerald-500 text-white'
                    : 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                }`}
              >
                {snippetCopied ? (
                  <>
                    <CheckCircle2 className="h-3 w-3" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" />
                    Copy
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-400">
              Select an account and API key above to generate the snippet.
            </div>
          )}

          <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-700">
            <strong>Security note:</strong> The API key in this snippet is visible to end-users
            (it runs in the browser). The key only allows posting opt-ins — it cannot read your
            data or make other changes. Use a dedicated key created specifically for widget use.
          </div>
        </div>

        {/* How opt-in compliance works */}
        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-slate-700">How compliance works</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              {
                title: 'Opt-out detection',
                desc: 'BrixLead automatically detects opt-out keywords (STOP, Unsubscribe, etc.) in inbound messages and removes the contact from broadcast audiences.',
              },
              {
                title: 'Broadcast filtering',
                desc: 'Before sending a broadcast, BrixLead filters the audience to only include contacts with an active opt-in record for this account.',
              },
              {
                title: 'Cooldown windows',
                desc: 'Contacts who received a marketing message recently may be on a cooldown to avoid over-messaging and quality degradation.',
              },
              {
                title: 'Audit trail',
                desc: 'Every opt-in, opt-out, and consent change is recorded in the audit log. Export the CSV for regulatory compliance.',
              },
            ].map((item) => (
              <div key={item.title} className="rounded-lg bg-slate-50 p-3">
                <p className="text-xs font-semibold text-slate-700">{item.title}</p>
                <p className="mt-0.5 text-[11px] text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Single opt-in modal */}
      {showAddModal && selectedAccount && (
        <AddOptInModal
          accountId={selectedAccount}
          onClose={() => setShowAddModal(false)}
          onAdded={() => setShowAddModal(false)}
        />
      )}
    </DashboardLayout>
  );
}
